import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../../config';
import Loader from '../../components/ui/Loader';
import { useAuth } from '../../components/shared/AuthContext';
import { debounce } from '../../utils/debounce';
import axios from 'axios';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import { ReportHeader, ReportSummaryCard } from "../../components/reports";

// Format date to DD-MM-YYYY
const formatDate = (date) => {
  if (!date || date === 'N/A') return 'N/A';
  if (!(date instanceof Date) || isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Parse DD-MM-YYYY string to Date object
const parseDate = (dateString) => {
  if (!dateString || dateString === 'N/A') return null;
  try {
    const [day, month, year] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  } catch {
    return null;
  }
};

const DemandCollectionReport = () => {
  const { roleName, data } = useAuth();
  const branchId = data?.branchId || null;
  const isSuperAdmin = roleName === 'superadmin';
  const [allEmis, setAllEmis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [originalData, setOriginalData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState(null);
  
  // Custom toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  // Filtering logic
  const applyFilter = useCallback(
    (emis) => {
      if (!emis || !Array.isArray(emis) || emis.length === 0) return [];
      if (filterType === 'today') {
        const today = new Date();
        const todayStr = formatDate(today);
        return emis.filter((emi) => emi.dueDate === todayStr);
      } else if (filterType === 'range' && startDate && endDate) {
        const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        return emis.filter((emi) => {
          const dueDate = parseDate(emi.dueDate);
          return dueDate && dueDate >= start && dueDate <= end;
        });
      }
      return emis;
    },
    [filterType, startDate, endDate]
  );

  // Fetch data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (!isSuperAdmin && branchId) params.branchId = branchId;
      const response = await axios.get(`${API_BASE_URL}/api/getdemand_collection_report`, { params });
      const result = response.data;
      if (result.status !== 'success' || !result.data || !result.data.customerReports) {
        throw new Error('Invalid response structure or no customer reports found');
      }
      const filteredCustomerReports = result.data.customerReports;
      if (!Array.isArray(filteredCustomerReports)) {
        throw new Error('Customer reports is not an array');
      }
      const emis = filteredCustomerReports.flatMap((customer) => {
        if (!customer.loanStatements || !Array.isArray(customer.loanStatements)) return [];
        return customer.loanStatements.map((statement) => ({
          id: statement.id || `temp-${Math.random()}`,
          memberId: customer.customerInfo?.ApplicantId || 'N/A',
          customerUniqueId: customer.customerInfo?.customerUniqueId || 'N/A',
          customerName: customer.customerInfo?.customerName || 'Unknown',
          branchId: customer.customerInfo?.branchId || 'N/A',
          employeeName: customer.customerInfo?.employeeName || 'N/A',
          employeeCode: customer.customerInfo?.employeeCode || 'N/A',
          dueDate: statement.dueDate || 'N/A',
          total: statement.total || 0,
          principal: statement.principal || 0,
          interest: statement.interest || 0,
          outstanding: statement.outstanding || 0,
          dueNo: statement.dueNo || 'N/A',
          phoneNumber: customer.customerInfo?.mobileNumber || 'N/A',
          email: 'N/A',
          received: statement.received || 'Not Received',
          accountBalance: 0,
          arrearPrincipal: statement.arrearPrincipal || 0,
          arrearInterest: statement.arrearInterest || 0,
          arrearTotal: statement.arrearTotal || 0,
          penalty: statement.penalty || 0,
          emiReceived: statement.emiReceived || false,
          paymentDate: statement.paymentDate || 'N/A',
        }));
      });
      setOriginalData(emis);
      setSelectedCustomerData(filteredCustomerReports);
      setAllEmis(applyFilter(emis));
    } catch (error) {
      setError(`Failed to load data: ${error.message}`);
      setAllEmis([]);
      setOriginalData([]);
      setSelectedCustomerData([]);
    } finally {
      setLoading(false);
    }
  }, [applyFilter, isSuperAdmin, branchId]);

  const debouncedFetch = useMemo(() => debounce(fetchDashboardData, 500), [fetchDashboardData]);

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);

  useEffect(() => {
    setAllEmis(applyFilter(originalData));
    setCurrentPage(1);
  }, [originalData, applyFilter]);

  // Calculate stats from filtered EMIs
  const stats = useMemo(() => {
    const filteredLoans = allEmis;
    const totalPrincipal = filteredLoans.reduce((sum, statement) => sum + parseFloat(statement.principal || 0), 0);
    const totalInterest = filteredLoans.reduce((sum, statement) => sum + parseFloat(statement.interest || 0), 0);
    const totalCollectedAmount = filteredLoans.filter((statement) => statement.received === 'Received').reduce((sum, statement) => sum + parseFloat(statement.total || 0), 0);
    const totalCollectedPrincipal = filteredLoans.filter((statement) => statement.received === 'Received').reduce((sum, statement) => sum + parseFloat(statement.principal || 0), 0);
    const totalCollectedInterest = filteredLoans.filter((statement) => statement.received === 'Received').reduce((sum, statement) => sum + parseFloat(statement.interest || 0), 0);
    const pendingPrincipal = filteredLoans.filter((statement) => statement.received !== 'Received').reduce((sum, statement) => sum + parseFloat(statement.principal || 0), 0);
    const pendingInterest = filteredLoans.filter((statement) => statement.received !== 'Received').reduce((sum, statement) => sum + parseFloat(statement.interest || 0), 0);
    return {
      totalPrincipal,
      totalInterest,
      totalCollectedAmount,
      totalCollectedPrincipal,
      totalCollectedInterest,
      pendingPrincipal,
      pendingInterest,
    };
  }, [allEmis]);

  // Pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = allEmis.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(allEmis.length / rowsPerPage);

  // Table data
  const receivedEmis = useMemo(() => currentRows.filter((emi) => emi.received === 'Received'), [currentRows]);
  const pendingEmis = useMemo(() => currentRows.filter((emi) => emi.received !== 'Received'), [currentRows]);

  // Customer modal
  const handleCustomerClick = (customerId) => {
    if (customerId === selectedCustomer) {
      setSelectedCustomer(null);
      setIsModalOpen(false);
    } else {
      setSelectedCustomer(customerId);
      setIsModalOpen(true);
    }
    setCurrentPage(1);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
  };

  // Export to Excel
  const exportToExcel = () => {
    const dataToExport = selectedCustomer
      ? allEmis.filter((emi) => emi.customerUniqueId === selectedCustomer)
      : allEmis;
    if (dataToExport.length === 0) {
      showToast('No data available to export.', 'warning');
      return;
    }
    const data = dataToExport.map((emi, index) => ({
      'S.No': index + 1,
      'Customer ID': emi.customerUniqueId,
      'Customer Name': emi.customerName,
      'Branch ID': emi.branchId,
      'Employee Name': emi.employeeName || 'N/A',
      'Employee Code': emi.employeeCode || 'N/A',
      'Due No': emi.dueNo,
      'Due Date': emi.dueDate,
      'Phone Number': emi.phoneNumber,
      'Total (₹)': parseFloat(emi.total || 0).toFixed(2),
      'Principal (₹)': parseFloat(emi.principal || 0).toFixed(2),
      'Interest (₹)': parseFloat(emi.interest || 0).toFixed(2),
      'Outstanding (₹)': parseFloat(emi.outstanding || 0).toFixed(2),
      'Arrear Principal (₹)': parseFloat(emi.arrearPrincipal || 0).toFixed(2),
      'Arrear Interest (₹)': parseFloat(emi.arrearInterest || 0).toFixed(2),
      'Arrear Total (₹)': parseFloat(emi.arrearTotal || 0).toFixed(2),
      'Penalty (₹)': parseFloat(emi.penalty || 0).toFixed(2),
      Status: emi.received,
      'Payment Date': emi.paymentDate,
    }));
    const worksheetData = [
      [
        'S.No',
        'Customer ID',
        'Customer Name',
        'Branch ID',
        'Employee Name',
        'Employee Code',
        'Due No',
        'Due Date',
        'Phone Number',
        'Total (₹)',
        'Principal (₹)',
        'Interest (₹)',
        'Outstanding (₹)',
        'Arrear Principal (₹)',
        'Arrear Interest (₹)',
        'Arrear Total (₹)',
        'Penalty (₹)',
        'Status',
        'Payment Date',
      ],
      ...data.map((row) => Object.values(row)),
      [],
      [
        'Total Amount Due:',
        '', '', '', '', '', '',
        dataToExport.reduce((sum, emi) => sum + parseFloat(emi.total || 0), 0).toFixed(2),
      ],
      [
        'Total Principal:',
        '', '', '', '', '', '',
        dataToExport.reduce((sum, emi) => sum + parseFloat(emi.principal || 0), 0).toFixed(2),
      ],
      [
        'Total Interest:',
        '', '', '', '', '', '',
        dataToExport.reduce((sum, emi) => sum + parseFloat(emi.interest || 0), 0).toFixed(2),
      ],
    ];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Loan Report');
    XLSX.writeFile(workbook, `Loan_Report_${formatDate(new Date())}.xlsx`);
    showToast('Exported to Excel successfully!', 'success');
  };

  // Customer select options
  const customerOptions = useMemo(() => {
    const uniqueIds = [...new Set(allEmis.map((emi) => emi.customerUniqueId))];
    return uniqueIds.map((customerId) => ({
      id: customerId,
      name: allEmis.find((emi) => emi.customerUniqueId === customerId)?.customerName || customerId,
    }));
  }, [allEmis]);

  // Modal details
  const selectedCustomerDetails = useMemo(
    () =>
      selectedCustomer
        ? selectedCustomerData?.find(
          (customer) => customer.customerInfo?.customerUniqueId === selectedCustomer
        )
        : null,
    [selectedCustomer, selectedCustomerData]
  );

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      <ReportHeader
        className="mb-4"
        filters={
          <>
            {/* Filter Type */}
            <div className="relative min-w-[150px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Filter Type
              </label>
              <div className="relative">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="all">All Records</option>
                  <option value="today">Today</option>
                  <option value="range">Date Range</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {filterType === 'range' && (
              <>
                {/* Start Date */}
                <div className="w-[145px] mt-1">
                  <DatePicker
                    label="Start Date"
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    placeholder="DD-MM-YYYY"
                  />
                </div>

                {/* End Date */}
                <div className="w-[145px] mt-1">
                  <DatePicker
                    label="End Date"
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    placeholder="DD-MM-YYYY"
                  />
                </div>
              </>
            )}

            {/* Select Customer */}
            <div className="relative min-w-[200px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Select Customer
              </label>
              <div className="relative">
                <select
                  value={selectedCustomer || ''}
                  onChange={(e) => handleCustomerClick(e.target.value)}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="">All Customers</option>
                  {customerOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name} ({option.id})
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </>
        }
        actions={
          <Button
            onClick={exportToExcel}
            variant="primary"
            size="sm"
            disabled={allEmis.length === 0}
            className="shadow-sm"
          >
            <i className="fa-solid fa-file-excel mr-1.5"></i>
            Export to Excel
          </Button>
        }
      />
      {loading && <Loader message="Loading loan data..." />}

      {error && (
        <div className="bg-rose-50 text-rose-700 p-4 rounded-xl border border-rose-200 text-sm font-medium mb-4">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2.5 mb-4">
        {[
          { label: 'Total Principal', value: `₹${(stats.totalPrincipal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, bg: 'bg-[#FFF0C7]', border: 'border-[#D97706]/25', accent: 'text-[#D97706]', icon: 'fa-money-bill-wave' },
          { label: 'Total Interest', value: `₹${(stats.totalInterest || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, bg: 'bg-[#E9DFFF]', border: 'border-[#7C3AED]/25', accent: 'text-[#7C3AED]', icon: 'fa-percent' },
          { label: 'Collected Amount', value: `₹${(stats.totalCollectedAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, bg: 'bg-[#DDF5E6]', border: 'border-[#16A34A]/25', accent: 'text-[#16A34A]', icon: 'fa-hand-holding-dollar' },
          { label: 'Collected Principal', value: `₹${(stats.totalCollectedPrincipal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, bg: 'bg-[#DCEBFF]', border: 'border-[#2563EB]/25', accent: 'text-[#2563EB]', icon: 'fa-wallet' },
          { label: 'Collected Interest', value: `₹${(stats.totalCollectedInterest || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, bg: 'bg-[#D9F3F7]', border: 'border-[#0891B2]/25', accent: 'text-[#0891B2]', icon: 'fa-coins' },
          { label: 'Pending Principal', value: `₹${(stats.pendingPrincipal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, bg: 'bg-[#E9DFFF]', border: 'border-[#7C3AED]/25', accent: 'text-[#7C3AED]', icon: 'fa-clock' },
          { label: 'Pending Interest', value: `₹${(stats.pendingInterest || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, bg: 'bg-[#FFF0C7]', border: 'border-[#D97706]/25', accent: 'text-[#D97706]', icon: 'fa-hourglass-half' },
        ].map((card, idx) => (
          <ReportSummaryCard
            key={idx}
            {...card}
          />
        ))}
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        {/* Collected Payments */}
        <div className="flex flex-col bg-white rounded-2xl border border-border-light shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <h3 className="text-xs font-black uppercase tracking-wider text-white">
              Collected Payments
            </h3>
          </div>
          <div className="p-2">
            <Table>
              <Table.Head>
                <tr>
                  <Table.HeadCell align="center">S.No</Table.HeadCell>
                  <Table.HeadCell align="center">Customer ID</Table.HeadCell>
                  <Table.HeadCell align="left">Customer Name</Table.HeadCell>
                  <Table.HeadCell align="right">Principal</Table.HeadCell>
                  <Table.HeadCell align="right">Interest</Table.HeadCell>
                  <Table.HeadCell align="right">Total</Table.HeadCell>
                  <Table.HeadCell align="center">Payment Date</Table.HeadCell>
                  <Table.HeadCell align="left">Employee</Table.HeadCell>
                </tr>
              </Table.Head>
              <Table.Body>
                {receivedEmis.length > 0 ? (
                  receivedEmis.map((emi, idx) => (
                    <Table.Row key={emi.id}>
                      <Table.Cell align="center">{indexOfFirstRow + idx + 1}</Table.Cell>
                      <Table.Cell align="center">
                        <button
                          onClick={() => handleCustomerClick(emi.customerUniqueId)}
                          className="text-xs font-bold text-primary hover:underline"
                        >
                          {emi.customerUniqueId}
                        </button>
                      </Table.Cell>
                      <Table.Cell align="left" className="font-semibold text-ink-800">{emi.customerName}</Table.Cell>
                      <Table.Cell align="right" className="font-semibold">₹{parseFloat(emi.principal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Table.Cell>
                      <Table.Cell align="right" className="font-semibold">₹{parseFloat(emi.interest || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Table.Cell>
                      <Table.Cell align="right" className="font-bold text-emerald-600">₹{parseFloat(emi.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Table.Cell>
                      <Table.Cell align="center">{emi.paymentDate || 'N/A'}</Table.Cell>
                      <Table.Cell align="left">
                        <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                          {emi.employeeName || 'N/A'}
                        </span>
                      </Table.Cell>
                    </Table.Row>
                  ))
                ) : (
                  <Table.EmptyRow colSpan={8} message="No received payments found" />
                )}
              </Table.Body>
            </Table>
          </div>
        </div>

        {/* Demand Payments */}
        <div className="flex flex-col bg-white rounded-2xl border border-border-light shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-rose-500 to-rose-600 px-6 py-4">
            <h3 className="text-xs font-black uppercase tracking-wider text-white">
              Demand Payments
            </h3>
          </div>
          <div className="p-2">
            <Table>
              <Table.Head>
                <tr>
                  <Table.HeadCell align="center">S.No</Table.HeadCell>
                  <Table.HeadCell align="center">Customer ID</Table.HeadCell>
                  <Table.HeadCell align="left">Customer Name</Table.HeadCell>
                  <Table.HeadCell align="right">Principal</Table.HeadCell>
                  <Table.HeadCell align="right">Interest</Table.HeadCell>
                  <Table.HeadCell align="right">Total</Table.HeadCell>
                  <Table.HeadCell align="center">Due Date</Table.HeadCell>
                  <Table.HeadCell align="left">Employee</Table.HeadCell>
                </tr>
              </Table.Head>
              <Table.Body>
                {pendingEmis.length > 0 ? (
                  pendingEmis.map((emi, idx) => (
                    <Table.Row key={emi.id}>
                      <Table.Cell align="center">{indexOfFirstRow + idx + 1}</Table.Cell>
                      <Table.Cell align="center">
                        <button
                          onClick={() => handleCustomerClick(emi.customerUniqueId)}
                          className="text-xs font-bold text-rose-600 hover:underline"
                        >
                          {emi.customerUniqueId}
                        </button>
                      </Table.Cell>
                      <Table.Cell align="left" className="font-semibold text-ink-800">{emi.customerName}</Table.Cell>
                      <Table.Cell align="right" className="font-semibold">₹{parseFloat(emi.principal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Table.Cell>
                      <Table.Cell align="right" className="font-semibold">₹{parseFloat(emi.interest || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Table.Cell>
                      <Table.Cell align="right" className="font-bold text-rose-600">₹{parseFloat(emi.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Table.Cell>
                      <Table.Cell align="center" className="text-rose-600 font-semibold">{emi.dueDate}</Table.Cell>
                      <Table.Cell align="left">
                        <span className="bg-rose-50 text-rose-700 border border-rose-100 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                          {emi.employeeName || 'N/A'}
                        </span>
                      </Table.Cell>
                    </Table.Row>
                  ))
                ) : (
                  <Table.EmptyRow colSpan={8} message="No pending payments found" />
                )}
              </Table.Body>
            </Table>
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      {allEmis.length > 0 && (
        <div className="flex items-center justify-between mt-6 bg-white p-3 rounded-xl border border-border-light shadow-sm">
          <div className="flex items-center gap-2 text-xs text-ink-500">
            <span>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-transparent border border-border-medium rounded px-1.5 py-1 focus:outline-none font-semibold text-ink-700"
            >
              {[5, 10, 20, 50].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <span className="text-xs text-ink-500">
            Showing {indexOfFirstRow + 1} - {Math.min(indexOfLastRow, allEmis.length)} of {allEmis.length}
          </span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl border border-border-light shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-light bg-canvas-muted">
              <h3 className="text-xs font-black uppercase tracking-wider text-ink-900">
                Customer Details
              </h3>
              <button
                onClick={closeModal}
                className="text-ink-400 hover:text-ink-600 transition-colors p-1.5 rounded-lg hover:bg-canvas-muted"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-grow">
              {selectedCustomerDetails ? (
                <div className="flex flex-col gap-6">
                  {/* Info Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 bg-canvas-muted p-4 rounded-xl border border-border-light">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wide">Name</span>
                      <span className="text-sm font-semibold text-ink-800">{selectedCustomerDetails.customerInfo?.customerName || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wide">Customer ID</span>
                      <span className="text-sm font-semibold text-primary">{selectedCustomerDetails.customerInfo?.customerUniqueId || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wide">Applicant ID</span>
                      <span className="text-sm font-semibold text-ink-800">{selectedCustomerDetails.customerInfo?.ApplicantId || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wide">Mobile Number</span>
                      <span className="text-sm font-semibold text-ink-800">{selectedCustomerDetails.customerInfo?.mobileNumber || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wide">Branch ID</span>
                      <span className="text-sm font-semibold text-ink-800">{selectedCustomerDetails.customerInfo?.branchId || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Statements Table */}
                  <div className="flex flex-col">
                    <h4 className="text-xs font-black uppercase tracking-wider text-ink-700 mb-3">
                      Loan Statements
                    </h4>
                    <Table>
                      <Table.Head>
                        <tr>
                          <Table.HeadCell align="center">Due No</Table.HeadCell>
                          <Table.HeadCell align="center">Due Date</Table.HeadCell>
                          <Table.HeadCell align="right">Principal</Table.HeadCell>
                          <Table.HeadCell align="right">Interest</Table.HeadCell>
                          <Table.HeadCell align="right">Total</Table.HeadCell>
                          <Table.HeadCell align="center">Status</Table.HeadCell>
                          <Table.HeadCell align="center">Payment Date</Table.HeadCell>
                        </tr>
                      </Table.Head>
                      <Table.Body>
                        {selectedCustomerDetails.loanStatements?.filter((statement) => {
                          if (filterType === 'today') {
                            const today = new Date();
                            const todayStr = formatDate(today);
                            return statement.dueDate === todayStr;
                          } else if (filterType === 'range' && startDate && endDate) {
                            const dueDate = parseDate(statement.dueDate);
                            const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                            const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
                            return dueDate && dueDate >= start && dueDate <= end;
                          }
                          return true;
                        }).map((statement) => (
                          <Table.Row key={statement.id || `temp-${Math.random()}`}>
                            <Table.Cell align="center">{statement.dueNo || 'N/A'}</Table.Cell>
                            <Table.Cell align="center">{statement.dueDate || 'N/A'}</Table.Cell>
                            <Table.Cell align="right">₹{parseFloat(statement.principal || 0).toFixed(2)}</Table.Cell>
                            <Table.Cell align="right">₹{parseFloat(statement.interest || 0).toFixed(2)}</Table.Cell>
                            <Table.Cell align="right" className="font-bold text-ink-800">₹{parseFloat(statement.total || 0).toFixed(2)}</Table.Cell>
                            <Table.Cell align="center">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                statement.received === 'Received'
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : 'bg-rose-50 text-rose-700 border border-rose-200'
                              }`}>
                                {statement.received || 'Not Received'}
                              </span>
                            </Table.Cell>
                            <Table.Cell align="center">{statement.paymentDate || 'N/A'}</Table.Cell>
                          </Table.Row>
                        )) || (
                          <Table.EmptyRow colSpan={7} message="No loan statements available" />
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-ink-500 font-medium">
                  No customer details available
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-canvas-muted border-t border-border-light flex justify-end">
              <Button variant="secondary" onClick={closeModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Toast Alert */}
      {toast.show && (
        <div className="fixed bottom-5 right-5 z-50 animate-in fade-in slide-in-from-bottom-5">
          <div className={`px-4 py-3 rounded-xl shadow-lg border text-sm font-semibold flex items-center gap-2 ${
            toast.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-amber-50 text-amber-800 border-amber-200'
          }`}>
            <i className={`fa-solid ${toast.type === 'success' ? 'fa-circle-check text-emerald-600' : 'fa-circle-exclamation text-amber-600'}`}></i>
            {toast.message}
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default DemandCollectionReport;